import { useEffect, useRef } from 'react';

import { useMainCta, type MainCtaConfig } from '@/contexts/mainCta';

/**
 * Register a primary call-to-action for the current screen. The CTA is
 * rendered in-document by `<MainCta />` inside `AppShell`, sitting above the
 * `<TabBar />`. The hook auto-clears on unmount so each route can declare
 * its own CTA without collisions.
 *
 * `onClick` is wrapped in a ref so an unstable callback identity (common when
 * the route does not memoize via `useCallback`) does not retrigger the
 * register/unregister effect — which would loop with the context state update.
 */
export function useMainButton({ text, enabled = true, loading = false, onClick }: MainCtaConfig) {
  const { setCta } = useMainCta();
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  useEffect(() => {
    setCta({
      text,
      enabled,
      loading,
      onClick: () => onClickRef.current(),
    });
    return () => setCta(null);
  }, [text, enabled, loading, setCta]);
}
